import React from "react";
import { Part, PartAction } from "./PartsList";
import Action from "../../redux/reducers/models/action";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import Close from "@material-ui/icons/Close";
import { useState, useEffect } from "react";
import { withNamespaces, WithNamespaces } from "react-i18next";
import format from "../../utils/formatter";
import { FullImage } from "../configuration/ConfigurationView";
import store from "../../redux/store";
import addPartAction from '../../redux/async-actions/addPart';
import removePartAsync from '../../redux/async-actions/removePart';

export interface PartDetailDialogProps {
	selectedPart: Part;
	closeDialog: () => void;
	applyParts: (parts: string[]) => Action;
	setImageTimestamp: () => Action;
	setConfigurationImages: (images: FullImage[]) => Action;
	appliedParts: Part[];
}

type PartDetailAllProps = PartDetailDialogProps & WithNamespaces;

export const PartDetailDialog: React.FunctionComponent<PartDetailAllProps> = ({
	t,
	applyParts,
	selectedPart,
	closeDialog,
	appliedParts,
	setImageTimestamp,
}) => {
	const otherImages = selectedPart.Images.Other;
	const allImages = [selectedPart.Images.Main, ...selectedPart.Images.Other];
	const partAction = PartAction;
	const isIncluded = appliedParts.includes(selectedPart);
	const [imageUrl, setImageUrl] = useState(selectedPart.Images.Main);
	const [itemAction, setItemAction] = useState<false | PartAction>(false);

	useEffect(() => {
		if (!itemAction) {
			return;
		}
		const addPart = (part: Part) => {
			store.dispatch(addPartAction(part.PartId) as any);
		};
		const removePart = (part: Part) => {
			store.dispatch(removePartAsync(part.PartId) as any);
		};
		const operation = {
			[partAction.Apply]: addPart.bind(null, selectedPart, false),
			[partAction.Remove]: removePart.bind(null, selectedPart, false),
		};

		operation[itemAction]();
	}, [
		itemAction,
		applyParts,
		selectedPart,
		partAction.Apply,
		partAction.Remove,
		setImageTimestamp
	]);

	return (
		<Grid
			container
			justify="center"
			alignContent="center"
			className="full-height"
		>
			<Grid item id="dialog-window-wrapper">
				<Grid container justify="space-between">
					<Grid item>
						<h2 id="part-detail-title">
							{selectedPart.Name}
							{selectedPart.Racing && <span className="racingComponent">{t('configuration.summary.ece')}</span>}
						</h2>
						<h3 id="part-id-title">{selectedPart.PartId}</h3>
					</Grid>
					<Grid item>
						<IconButton title={t("buttons.closeDialog")} onClick={closeDialog}>
							<Close />
						</IconButton>
					</Grid>
				</Grid>
				<Grid
					container
					direction="column"
					justify="space-between"
					id="dialog-content-wrapper"
				>
					<Grid container>
						<Grid item xs={6}>
							{/* main image */}
							<div id="main-dialog-image-wrapper">
								<img
									id="main-dialog-image"
									src={imageUrl}
									alt="part"
								/>
							</div>
							{/* other images */}
							{otherImages.length > 0 &&
								allImages.map((url: string, idx: number) => {
									const selected = url === imageUrl ? "selected" : "";
									return (
										<div
											key={idx}
											className={`other-dialog-image-wrapper ${selected}`}
										>
											<img
												className={`clickable other-dialog-image`}
												onClick={() => setImageUrl(url)}
												src={url}
												alt="other"
											/>
										</div>
									);
								})}
						</Grid>
						{/* description */}
						<Grid item xs={6}>
							<div
								id="part-detail-description"
								dangerouslySetInnerHTML={{ __html: selectedPart.Description }}
							/>
							{!!selectedPart.ApprovalInfo && (
								<>
									<h4 className="mt-10">Approval</h4>{/* TODO: move into language file */}
									<p className="mt-2 text-orange">{selectedPart.ApprovalInfo}</p>
								</>
							)}
						</Grid>
						{/* price & action */}
						<Grid item xs={12}>
							<Grid
								container
								className="full-height"
								direction="column"
								justify="flex-end"
							>
								<Grid container justify="flex-end">
									<div className="part-price part-price-in-dialog">
										{format.currency(selectedPart.Price)}
									</div>
									<button
										onClick={() =>
											setItemAction(
												isIncluded ? partAction.Remove : partAction.Apply
											)
										}
										className={isIncluded ? "remove" : "primary"}
									>
										{isIncluded
											? t("configuration.parts.remove")
											: t("configuration.parts.add")}
									</button>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

export default withNamespaces()(PartDetailDialog);
